/**
 * PositionsTable — the plainest question an advisor asks, and the container it had no answer in
 * (TASK-908).
 *
 * Replayed 2026-08-31:
 *
 *   "Show Schneider's largest positions with market values"        -> generic Portfolio Overview
 *   "List Schneider's five largest positions with their exact
 *    market values, and his total cash."                           -> answered in WEIGHTS
 *
 * The second is the one that shows what was missing. The assistant's ranking was exactly right —
 * ZKB Gold 5.2%, Swiss Conf 2030 3.8%, Partners Group 3.5%, Kontokorrent 2.9%, Swiss Conf 2037
 * 2.9%, matching `/positions/` to the cent — and it said so itself: *"the analysis shows weight
 * percentages rather than exact market values… you would need to view the detailed portfolio
 * holdings directly."* It had the data and no table to put it in.
 *
 * Split presentational / connected, the TASK-901 `TradeDelta` pattern: the ranking and the share
 * arithmetic are the part worth testing, and they should not need a QueryClient to reach.
 *
 * **Known limit: this totals POSITIONS, not the book.** `/positions/` reads
 * `portfolio_positions` only, and `PretimAdapter._sync_positions` routes cash rows to
 * `cash_positions` instead (`pretim_adapter.py:501-519`, `continue  # Don't save as regular
 * position`). So on a Pretim-synced portfolio there is no cash row here, the header total is
 * the sum of the securities, and the shares are shares OF THAT. It reconciles with
 * `/positions/` to the franc, which is what this ticket asks for, and it can disagree with
 * `/allocation/` and `/drift/`, which also read `get_cash_positions`.
 *
 * Do NOT "fix" this by also fetching `/cash/{id}` and merging. `get_cash_view`'s docstring
 * (`portfolio_repository.py:463-502`) records TASK-878 getting that wrong twice in opposite
 * directions — merging collapsed Ammann's two CHF 100'000 accounts into one row and reported
 * half his cash; picking one source for the whole query dropped an uploaded portfolio's cash
 * entirely. The rule it landed on is ONE SOURCE PER PORTFOLIO, never a merge, and it lives in
 * the repository for that reason. The prompt's "and his total cash" half is `/cash/{id}`'s
 * answer, not this table's.
 *
 * **Values, not weights, and the shares are computed on the CHF figure.** `/positions/` sorts by
 * `market_value`, which is the LOCAL number; on a multi-currency book "largest by CHF" is a
 * different order. Dividing a local numerator by a CHF total is the TASK-865 defect verbatim
 * (`main.py:487-492` records it overstating a EUR position by 1/0.96), so both the ranking and
 * the percentage read `market_value_base`. A row whose base value is `null` — no FX rate, never
 * 0 and never par — is still SHOWN, and excluded from the shares, with the footer saying which
 * currencies could not be converted.
 */

import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { httpClient } from '../../../lib/httpClient';
import { WidgetErrorState } from '../../ui/WidgetErrorState';
import { WidgetContainer } from '../WidgetContainer';
import { OpenAsSpreadsheet } from './OpenAsSpreadsheet';

/** The house CHF formatter — `CHF 535'487`, the apostrophe group and no centimes. */
const chf = (v: number) =>
  new Intl.NumberFormat('de-CH', {
    style: 'currency', currency: 'CHF', maximumFractionDigits: 0,
  }).format(v);

const pct = (v: number) => `${v.toFixed(1)}%`;

/** Absent, not zero — the TASK-872 rule, in the one place this widget needs it. */
const DASH = '—';

/** The API row, narrowed to what this table reads. `/positions/` returns `SELECT *`. */
export interface PositionRow {
  id?: string;
  position_id?: string;
  isin?: string | null;
  security_name?: string | null;
  asset_class?: string | null;
  market_value?: number | null;
  /** CHF. `null` when no FX rate existed — never 0. */
  market_value_base?: number | null;
  currency?: string | null;
}

export interface PositionsResponse {
  positions: PositionRow[];
  count?: number;
  base_currency?: string;
  unpriceable_currencies?: string[];
  timestamp?: string | null;
}

export interface RankedPosition {
  /** `id` is the column; `position_id` is the house name for it. */
  positionId: string;
  securityName: string;
  isin: string;
  assetClass: string;
  /** CHF, or null when unconvertible. */
  valueChf: number | null;
  /** Share of the CHF total, or null when this row has no CHF value. */
  sharePct: number | null;
}

export interface RankedPositions {
  rows: RankedPosition[];
  /** Every position the client holds, not just the rows shown. */
  totalCount: number;
  /** Sum of the CHF values that exist. */
  totalChf: number;
  /** True when at least one position could not be converted. */
  hasUnpriced: boolean;
}

/** Default when the prompt names no `top N`. Raised by re-prompting with a number —
 *  there is no on-tile expander, and saying otherwise would be a claim with no control
 *  behind it. */
export const DEFAULT_TOP_N = 10;

/**
 * Rank by CHF value and compute each row's share of the CHF total.
 *
 * Exported because this is the part that can be wrong: the order, the denominator, and what
 * happens to a row with no CHF value.
 */
export function rankPositions(
  positions: PositionRow[],
  topN: number = DEFAULT_TOP_N,
): RankedPositions {
  const all = positions ?? [];
  const valueOf = (p: PositionRow) =>
    typeof p.market_value_base === 'number' && Number.isFinite(p.market_value_base)
      ? p.market_value_base
      : null;

  const totalChf = all.reduce((sum, p) => sum + (valueOf(p) ?? 0), 0);
  const hasUnpriced = all.some((p) => valueOf(p) === null);

  const rows = [...all]
    // Descending by CHF. A row with no CHF value cannot be ranked against one that has it, so
    // it sorts last rather than being treated as zero. Nulls are compared FIRST, because
    // `-Infinity - -Infinity` is NaN — an invalid comparator, and the order of two unconvertible
    // positions would have been unspecified rather than stable.
    .sort((a, b) => {
      const [x, y] = [valueOf(a), valueOf(b)];
      if (x === null && y === null) return 0;
      if (x === null) return 1;
      if (y === null) return -1;
      return y - x;
    })
    .slice(0, Math.max(1, topN))
    .map((p) => {
      const valueChf = valueOf(p);
      return {
        positionId: String(p.position_id ?? p.id ?? ''),
        securityName: p.security_name || 'Unnamed position',
        isin: p.isin || DASH,
        assetClass: p.asset_class || DASH,
        valueChf,
        sharePct: valueChf !== null && totalChf > 0 ? (valueChf / totalChf) * 100 : null,
      };
    });

  return { rows, totalCount: all.length, totalChf, hasUnpriced };
}

export interface PositionsTableProps {
  clientId?: string;
  clientName?: string;
  ranked: RankedPositions | null;
  unpriceableCurrencies?: string[];
  dataAsOf?: string | null;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

export function PositionsTable({
  clientId,
  clientName,
  ranked,
  unpriceableCurrencies = [],
  dataAsOf = null,
  isLoading = false,
  isError = false,
  error = null,
  onRetry,
}: PositionsTableProps) {
  const body = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 p-6 text-xs text-muted-foreground"
             data-testid="positions-loading">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Reading the positions…
        </div>
      );
    }

    if (isError) {
      return (
        <WidgetErrorState
          title={`Couldn't load ${clientName ?? 'this client'}'s positions`}
          detail={error?.message}
          onRetry={onRetry}
        />
      );
    }

    if (!ranked || ranked.rows.length === 0) {
      return (
        <p className="p-6 text-xs text-muted-foreground" data-testid="positions-empty">
          No positions on file for {clientName ?? 'this client'} yet.
        </p>
      );
    }

    return (
      <div>
        {/* The header the ticket specifies: who, how many, how much. */}
        <div className="flex flex-wrap items-baseline justify-between gap-2 pb-2"
             data-testid="positions-header">
          <p className="text-xs font-medium">
            {clientName ?? 'Client'}
            <span className="text-muted-foreground">
              {' · '}{ranked.totalCount} position{ranked.totalCount === 1 ? '' : 's'}
              {/* When nothing converted, the total is not zero — it is unknown. Printing
                  `CHF 0` here would be the confident zero this file's own rule forbids. */}
              {' · '}{ranked.totalChf === 0 && ranked.hasUnpriced ? DASH : chf(ranked.totalChf)}
            </span>
          </p>
          <p className="text-[10px] text-muted-foreground">sorted by value</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="positions-table">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="py-1 text-left font-normal">security</th>
                <th className="py-1 text-left font-normal">isin</th>
                <th className="py-1 text-left font-normal">asset class</th>
                <th className="py-1 text-right font-normal">CHF</th>
                <th className="py-1 text-right font-normal">% of portfolio</th>
              </tr>
            </thead>
            <tbody>
              {ranked.rows.map((r) => (
                <tr
                  key={r.positionId || r.isin}
                  data-position-id={r.positionId}
                  data-testid="positions-row"
                  className="border-b border-border/40 last:border-0"
                >
                  <td className="max-w-[220px] truncate py-1" title={r.securityName}>
                    {r.securityName}
                  </td>
                  <td className="py-1 font-mono text-[10px]">{r.isin}</td>
                  <td className="py-1">{r.assetClass}</td>
                  <td className="py-1 text-right tabular-nums">
                    {r.valueChf === null ? DASH : chf(r.valueChf)}
                  </td>
                  <td className="py-1 text-right tabular-nums">
                    {r.sharePct === null ? DASH : pct(r.sharePct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {ranked.totalCount > ranked.rows.length && (
          <p className="pt-2 text-[10px] text-muted-foreground" data-testid="positions-truncated">
            Showing the top {ranked.rows.length} of {ranked.totalCount} by value.
          </p>
        )}

        {/* The honesty line. A CHF total that silently omits an unconvertible position is the
            TASK-865 / TASK-878 family of defect. */}
        {ranked.hasUnpriced && (
          <p className="pt-1 text-[10px] text-muted-foreground" data-testid="positions-unpriced">
            Some positions have no CHF value
            {unpriceableCurrencies.length > 0 && ` (${unpriceableCurrencies.join(', ')})`}
            {' '}and are excluded from the total and the shares.
          </p>
        )}
      </div>
    );
  };

  return (
    <WidgetContainer
      source="Portfolio · Positions"
      dataAsOf={dataAsOf}
      toolbar={
        ranked && ranked.rows.length > 0 ? (
          <OpenAsSpreadsheet
            sourceWidget="PositionsTable"
            clientId={clientId}
            asOf={dataAsOf}
            build={() => ({
              // TASK-876: a handover is a document plus a sentence. On screen the RM sees
              // "Showing the top N of M"; the colleague opening the workbook sees N rows and
              // cannot tell the rest exist unless the title says so.
              title: ranked.totalCount > ranked.rows.length
                ? `Positions — ${clientName ?? 'client'} (top ${ranked.rows.length} of `
                  + `${ranked.totalCount} by value)`
                : `Positions — ${clientName ?? 'client'}`,
              sheetName: 'Positions',
              columns: [
                { header: 'Security', width: 40 },
                { header: 'ISIN', width: 16 },
                { header: 'Asset class', width: 16 },
                { header: 'CHF', fmt: 'currency', width: 16 },
                // Stored as the fraction Excel's percent format expects — the same
                // multiply-by-100 trap DriftBars records at its own `build()`.
                { header: '% of portfolio', fmt: 'percent', width: 14 },
                { header: 'position_id', width: 38 },
              ],
              rows: ranked.rows.map((r) => [
                r.securityName,
                r.isin,
                r.assetClass,
                r.valueChf,
                r.sharePct === null ? null : r.sharePct / 100,
                r.positionId,
              ]),
            })}
          />
        ) : undefined
      }
    >
      {body()}
    </WidgetContainer>
  );
}

export interface PositionsTablePanelProps {
  /**
   * Required. With it optional the query was gated `enabled: !!clientId`, and in react-query v5
   * a disabled query is not `isLoading` — so the panel fell through to "No positions on file",
   * asserting an absence it had never checked. Every sibling widget types it required.
   */
  clientId: string;
  clientName?: string;
  /** From the prompt, via the filter contract. */
  filters?: { topN?: number };
  topN?: number;
}

/** The registry entry point: fetches, ranks, renders. */
export function PositionsTablePanel({
  clientId,
  clientName,
  filters,
  topN,
}: PositionsTablePanelProps) {
  const effectiveTopN = filters?.topN ?? topN ?? DEFAULT_TOP_N;

  const query = useQuery({
    // The key `SectorTreemap` already uses, so two tiles reading the same positions dedupe.
    queryKey: ['portfolio-positions', clientId],
    queryFn: () => httpClient.get<PositionsResponse>(`/api/portfolio/positions/${clientId}`),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const data = query.data;
  const ranked = data ? rankPositions(data.positions ?? [], effectiveTopN) : null;

  return (
    <PositionsTable
      clientId={clientId}
      clientName={clientName}
      ranked={ranked}
      unpriceableCurrencies={data?.unpriceable_currencies ?? []}
      dataAsOf={data?.timestamp ?? null}
      isLoading={query.isLoading}
      isError={query.isError}
      error={query.error as Error | null}
      onRetry={() => void query.refetch()}
    />
  );
}

export default PositionsTablePanel;
